import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@/v3-dashboard/components/UI/Modal';
import {
  getFeatureNotAvailableTitle,
  getFeatureSubtitleFreeToStarter,
} from '@/helpers/tier/featureTitles';
import FilterActions from '@/v3-dashboard/components/FilterActions';
import FreeToStarterPlanCard from './FreeToStarterPlanCard';
import useDanteApi from '@/hooks/useDanteApi';
import * as plansService from '@/services/plans.service';
import mixpanel from '@/utils/mixpanel';
import useModalStore from '@/stores/modal/modalStore';
import { getAddonConfigForFeature } from './addonConfigs';
import { FREE_TIER_PLANS, PERIOD_FILTERS } from '@/v3-dashboard/pages/Billing/tierFeatures';
import {
  UPGRADE_MODAL_CHECK_GREEN,
  UPGRADE_MODAL_CHECK_PURPLE,
  getUpgradeModalHighlightTier,
  getOrderedUpgradeModalFeatures,
} from './freeToStarterModalReferenceContent';
import useToast from '@/hooks/useToast';
import useTrackModalViewed from '@/hooks/useTrackModalViewed';
import DLoading from '@/components/DLoading';
import { useUserStore } from '@/stores/user/userStore';

function formatCurrency(amount, currency = 'USD') {
  if (amount == null) return null;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Short blurbs under price — modal-specific layout (see pricing modal design). */
const MODAL_PLAN_TAGLINES = {
  starter: 'See conversations, analytics, and connect your tools.',
  advanced: 'Multiple agents, your brand, lead gen, and WhatsApp.',
  pro: 'API access, advanced models, SLA, and full scale.',
};

const FreeToStarterModal = ({
  isOpen,
  onClose,
  feature,
  currentFeatureTitle,
  currentTier,
  targetTier,
}) => {
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const { addErrorToast } = useToast();
  const { data: plansData, isLoading: loadingPlans } = useDanteApi(plansService.getTiers);
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [loadingTierType, setLoadingTierType] = useState(null);

  const modalStore = useModalStore();
  const addonConfig = getAddonConfigForFeature(feature);
  const commonTrackingProps = {
    version: 'pricing-modal-v2',
    modal_variant: modalStore.modalVariant,
    modal_type: addonConfig ? 'plan_and_addon' : 'plan_only',
    current_tier: currentTier ?? user?.tier_type,
    target_tier: targetTier,
    feature_name: feature || null,
    feature_title: currentFeatureTitle || null,
    source: modalStore.upgradeContext?.source || null,
    addon_shown: addonConfig?.addonKey || null,
    addon_label: addonConfig?.label || null,
    plans_shown: 'starter_advanced_pro',
    selected_period: selectedPeriod,
  };

  const periodPlans = useMemo(() => {
    if (!plansData) return [];
    const list = plansData?.[selectedPeriod] ?? plansData?.monthly ?? [];
    return Array.isArray(list) ? list : [];
  }, [plansData, selectedPeriod]);

  const monthlyPlans = useMemo(() => {
    if (!plansData) return [];
    const list = plansData?.monthly ?? [];
    return Array.isArray(list) ? list : [];
  }, [plansData]);

  const highlightTier = useMemo(() => getUpgradeModalHighlightTier(feature), [feature]);

  const displayPlans = useMemo(() => {
    const tierByName = (list, name) => {
      const normalized = (name || '').toLowerCase();
      return list.find((tier) => (tier?.name || '').toLowerCase() === normalized);
    };
    return FREE_TIER_PLANS.map((plan) => {
      const backendTier = tierByName(periodPlans, plan.planTitle);
      const isYearly = selectedPeriod === 'yearly';
      const monthlyTier = isYearly ? tierByName(monthlyPlans, plan.planTitle) : null;
      const originalPrice =
        isYearly && monthlyTier?.price ? formatCurrency(monthlyTier.price * 12) : null;
      return {
        ...plan,
        tierId: backendTier?.id,
        price: formatCurrency(backendTier?.price) ?? plan.price,
        originalPrice,
        priceSuffix: isYearly ? '/year' : '/month',
      };
    });
  }, [selectedPeriod, periodPlans, monthlyPlans]);

  useTrackModalViewed(isOpen, modalStore.upgradeContext, commonTrackingProps);

  const handleDismiss = (method = 'close_button') => {
    mixpanel.track('Upgrade Path Modal Dismissed', {
      ...commonTrackingProps,
      dismiss_method: method,
    });
    onClose();
  };

  const handlePeriodChange = (newPeriod) => {
    mixpanel.track('Upgrade Path Modal Period Changed', {
      ...commonTrackingProps,
      previous_period: selectedPeriod,
      new_period: newPeriod,
    });
    setSelectedPeriod(newPeriod);
  };

  const handleSeeFullComparison = () => {
    mixpanel.track('Upgrade Path Modal Full Comparison Clicked', commonTrackingProps);
    onClose();
    navigate('/plans');
  };

  const handleSelectPlan = async (plan) => {
    if (!plan?.tierId) {
      mixpanel.track('Upgrade Path Modal Checkout Failed', {
        ...commonTrackingProps,
        target_tier: plan?.tierType,
        error_message: 'Plan checkout is not available yet.',
      });
      addErrorToast({ message: 'Plan checkout is not available yet. Please try again shortly.' });
      return;
    }
    try {
      setLoadingTierType(plan.tierType);
      mixpanel.track('Upgrade Path Modal CTA Clicked', {
        ...commonTrackingProps,
        cta_label: plan.buttonLabel,
        target_tier: plan.tierType,
        target_tier_id: plan.tierId,
        price: plan.price,
        plan_title: plan.planTitle,
      });
      const response = await plansService.getCheckoutSession(plan.tierId);
      if (response?.status === 200 && response?.data?.checkout_session?.url) {
        window.location.assign(response.data.checkout_session.url);
      }
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to start checkout. Please try again.';
      mixpanel.track('Upgrade Path Modal Checkout Failed', {
        ...commonTrackingProps,
        target_tier: plan.tierType,
        error_message: message,
      });
      addErrorToast({ message });
    } finally {
      setLoadingTierType(null);
    }
  };

  const title = getFeatureNotAvailableTitle(feature);
  const subtitle = getFeatureSubtitleFreeToStarter(feature);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      title={title}
      subtitle={subtitle}
      closeBackdrop
      centeredHeader
      headerClassName="!border-0 !px-6 !pt-7 !pb-0"
      closeButtonClassName="!right-6 !top-7"
      bodyClassName={loadingPlans ? '!overflow-y-hidden' : '!pt-0'}
      className="md:!min-h-[700px] md:!max-w-[min(1040px,calc(100vw-2rem))] md:!w-full md:!rounded-[14px]"
    >
      <div className="flex min-h-0 w-full flex-col items-stretch gap-v3-sm md:flex-1">
        <div className="flex w-full flex-wrap justify-center gap-v3-sm md:justify-center ">
          <FilterActions
            filters={PERIOD_FILTERS}
            value={selectedPeriod}
            onChange={handlePeriodChange}
          />
        </div>

        {loadingPlans ? (
          <div className="flex min-h-[320px] w-full items-center justify-center">
            <DLoading show height="h-auto" />
          </div>
        ) : (
          <div className="min-w-0 pb-1 pt-5">
            <div className="grid grid-cols-1  gap-v3-md md:gap-[14px] overflow-visible md:grid-cols-3 ">
              {displayPlans.map((plan) => {
                const { lines, featuredRowIndex } = getOrderedUpgradeModalFeatures(
                  plan.tierType,
                  feature
                );
                return (
                <div key={plan.tierType} className="flex min-w-0 overflow-visible w-full md:w-auto no-scrollbar animate-fadeInUp">
                  <FreeToStarterPlanCard
                    tierType={plan.tierType}
                    planTitle={plan.planTitle}
                    tagline={MODAL_PLAN_TAGLINES[plan.tierType] ?? plan.planDescription}
                    price={plan.price}
                    originalPrice={plan.originalPrice}
                    priceSuffix={plan.priceSuffix}
                    features={lines}
                    featuredRowIndex={featuredRowIndex}
                    showMostPopularBadge={plan.tierType === 'advanced'}
                    isHighlighted={plan.tierType === highlightTier}
                    checkmarkClassName={
                      plan.tierType === highlightTier
                        ? UPGRADE_MODAL_CHECK_PURPLE
                        : UPGRADE_MODAL_CHECK_GREEN
                    }
                    buttonLabel={plan.buttonLabel}
                    buttonLoading={loadingTierType === plan.tierType}
                    onButtonClick={() => handleSelectPlan(plan)}
                  />
                </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-col items-center border-t border-[#F3F4F6] pt-4 text-center">
          <p className="mb-1.5 font-inter-variable text-xs font-medium text-[#9CA3AF]">
            No contract. Instant activation. Cancel anytime.
          </p>
          <button
            type="button"
            onClick={handleSeeFullComparison}
            className="font-inter-variable text-[13px] font-semibold text-[#5745FA] hover:underline"
          >
            {'See all plans & features →'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default FreeToStarterModal;
